import { useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  Typography,
  List,
  ListItem,
  IconButton,
  Alert,
  CircularProgress,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloseIcon from '@mui/icons-material/Close';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB in bytes

const formatFileSize = (bytes) => {
  if (bytes >= 1024 * 1024) {
    return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
  }
  return `${(bytes / 1024).toFixed(1)} KB`;
};

const AnnexureUploadModal = ({ open, onSkip, onSubmit, isLoading, error }) => {
  const [files, setFiles] = useState([]);
  const [validationError, setValidationError] = useState(null);
  const fileInputRef = useRef(null);

  const resetState = () => {
    setFiles([]);
    setValidationError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleFileSelect = (event) => {
    const selected = Array.from(event.target.files || []);
    if (selected.length === 0) {
      return;
    }

    let errorMessage = null;
    const validFiles = [];

    selected.forEach((file) => {
      if (file.type !== 'application/pdf') {
        errorMessage = 'Only PDF files are accepted.';
        return;
      }
      if (file.size > MAX_FILE_SIZE) {
        errorMessage = 'Each file must not exceed 10MB.';
        return;
      }
      validFiles.push(file);
    });

    setValidationError(errorMessage);
    if (validFiles.length > 0) {
      setFiles((prev) => [...prev, ...validFiles]);
    }

    // Allow re-selecting the same file after removal
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSkip = () => {
    resetState();
    onSkip();
  };

  const handleSubmit = () => {
    onSubmit(files);
  };

  return (
    <Dialog
      open={open}
      onClose={isLoading ? undefined : handleSkip}
      maxWidth='sm'
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle>
        <Typography variant='h5' sx={{ fontWeight: 600 }}>
          Add Annexures
        </Typography>
        <Typography variant='body2' color='text.secondary'>
          Optionally attach PDF annexures to include with the SOW, or skip to
          generate without them.
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <input
          ref={fileInputRef}
          type='file'
          accept='.pdf'
          multiple
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />

        <Box
          sx={{
            p: 2,
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 2,
            textAlign: 'center',
          }}
        >
          <Typography variant='body2' color='text.secondary' sx={{ mb: 1.5 }}>
            PDF files only · Maximum 10MB per file
          </Typography>
          <Button
            variant='outlined'
            startIcon={<CloudUploadIcon />}
            onClick={() => fileInputRef.current?.click()}
            disabled={isLoading}
          >
            Choose PDF files
          </Button>
        </Box>

        {(validationError || error) && (
          <Alert severity='error' sx={{ mt: 2 }}>
            {validationError || error}
          </Alert>
        )}

        {files.length > 0 && (
          <List sx={{ mt: 2 }}>
            {files.map((file, index) => (
              <ListItem
                key={`${file.name}-${index}`}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  mb: 1,
                }}
                secondaryAction={
                  <IconButton
                    edge='end'
                    aria-label='Remove file'
                    onClick={() => handleRemoveFile(index)}
                    disabled={isLoading}
                  >
                    <CloseIcon />
                  </IconButton>
                }
              >
                <Box>
                  <Typography variant='body2' sx={{ fontWeight: 500 }}>
                    {file.name}
                  </Typography>
                  <Typography variant='caption' color='text.secondary'>
                    {formatFileSize(file.size)}
                  </Typography>
                </Box>
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
        <Button variant='text' onClick={handleSkip} disabled={isLoading}>
          Skip
        </Button>
        <Button
          variant='contained'
          onClick={handleSubmit}
          disabled={files.length === 0 || isLoading}
          startIcon={isLoading ? <CircularProgress size={16} /> : null}
        >
          Generate with Annexures
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AnnexureUploadModal;
